import React, { useState, useEffect } from 'react';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  Clock3,
  Eye,
  LoaderCircle,
  RefreshCw,
  Search,
  Settings2,
  SlidersHorizontal,
  UsersRound,
  X,
  type LucideIcon,
} from 'lucide-react';
import { formatDateTimeString } from '../lib/utils';
import { useServiceProviders } from '../hooks/useServiceProviders';
import { ServiceProvider, serviceProviderService } from '../services/serviceProviderService';
import { StatusUpdateModal } from './StatusUpdateModal';

// Use ServiceProvider interface from the service
type Mechanic = ServiceProvider;

// Mock data removed - now using real API data

interface RegisteredMechanicsProps {
  onViewMechanic: (mechanic: Mechanic) => void;
}

interface MetricCardProps {
  label: string;
  value: string;
  helper: string;
  icon: LucideIcon;
  iconClassName: string;
  iconBackgroundClassName: string;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, helper, icon: Icon, iconClassName, iconBackgroundClassName }) => (
  <article className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="flex items-start justify-between gap-4">
      <div>
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">{label}</p>
        <p className="mt-3 text-2xl font-semibold tracking-tight text-slate-950">{value}</p>
        <p className="mt-1 text-xs text-slate-500">{helper}</p>
      </div>
      <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${iconBackgroundClassName}`}>
        <Icon className={`h-5 w-5 ${iconClassName}`} aria-hidden="true" />
      </span>
    </div>
  </article>
);

const filterControlClassName = 'block h-11 w-full rounded-xl border border-slate-200 bg-slate-50/70 px-3 text-sm text-slate-800 shadow-sm transition placeholder:text-slate-400 focus-visible:border-blue-500 focus-visible:bg-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-100';

export const RegisteredMechanics: React.FC<RegisteredMechanicsProps> = ({ onViewMechanic }) => {
  const [areFiltersOpen, setAreFiltersOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'enabled' | 'disabled' | 'blocked'>('all');
  const [sortConfig, setSortConfig] = useState<{
    key: keyof Mechanic | null;
    direction: 'asc' | 'desc';
  }>({ key: null, direction: 'asc' });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [selectedMechanic, setSelectedMechanic] = useState<Mechanic | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Use the API hook
  const {
    serviceProviders: mechanics,
    loading,
    error,
    pagination,
    stats,
    refreshData,
    updateFilters,
    updateServiceProviderStatus,
    isUpdatingStatus,
  } = useServiceProviders({
    page: currentPage,
    limit: itemsPerPage,
    status: statusFilter,
    search: searchTerm,
  });

  // Update filters when search or status changes
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      
      updateFilters({
        page: 1, // Reset to first page when searching
        search: searchTerm,
        status: statusFilter,
        sortBy: sortConfig.key || undefined,
        sortOrder: sortConfig.key ? sortConfig.direction : undefined,
      });
      setCurrentPage(1);
    }, searchTerm ? 300 : 0); // Debounce search but not status changes

    return () => clearTimeout(timeoutId);
  }, [searchTerm, statusFilter, sortConfig, updateFilters]);

  // Update filters when pagination changes (but not when search/status changes)
  useEffect(() => {
    // Only update pagination if we're not in the middle of a search/status change
    if (currentPage > 1 || itemsPerPage !== 10) {
      updateFilters({
        page: currentPage,
        limit: itemsPerPage,
      });
    }
  }, [currentPage, itemsPerPage, updateFilters]);

  // Use API data directly since filtering is handled server-side
  // Ensure paginatedMechanics is always an array
  const paginatedMechanics = Array.isArray(mechanics) ? mechanics : [];
  const totalItems = pagination?.total || 0;
  const totalPages = pagination?.totalPages || 1;
  const startIndex = pagination ? (pagination.page - 1) * pagination.limit : 0;
  const endIndex = Math.min(startIndex + (pagination?.limit || 0), totalItems);
  const activeFilterCount = [Boolean(searchTerm.trim()), statusFilter !== 'all'].filter(Boolean).length;

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter]);

  const handleSort = (key: keyof Mechanic) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const getSortIcon = (columnKey: keyof Mechanic) => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />;
    }

    return sortConfig.direction === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 text-blue-700" aria-hidden="true" />
      : <ArrowDown className="h-3.5 w-3.5 text-blue-700" aria-hidden="true" />;
  };


  const getStatusBadgeClass = (status: Mechanic['status']) => {
    // Handle both numeric and string status values
    const statusValue = typeof status === 'number' ? status : status;
    
    switch (statusValue) {
      case 1:
      case 'enabled':
        return 'border-emerald-200 bg-emerald-50 text-emerald-700';
      case 0:
      case 'disabled':
        return 'border-amber-200 bg-amber-50 text-amber-700';
      case 'blocked':
        return 'border-red-200 bg-red-50 text-red-700';
      default:
        return 'border-slate-200 bg-slate-50 text-slate-700';
    }
  };

  const getStatusText = (status: Mechanic['status']) => {
    const statusValue = typeof status === 'number' ? status : status;
    
    switch (statusValue) {
      case 1:
        return 'Enabled';
      case 0:
        return 'Disabled';
      case 'enabled':
        return 'Enabled';
      case 'disabled':
        return 'Disabled';
      case 'blocked':
        return 'Blocked';
      default:
        return 'Unknown';
    }
  };

  const handleViewMechanic = async (mechanic: Mechanic) => {
    try {
      // Fetch full profile details using the profile API
      const response = await serviceProviderService.getServiceProviderProfile(mechanic.contactNumber);
      
      if (response.success && response.data?.serviceProvider) {
        // Convert PendingRegistration to ServiceProvider format
        const profile = response.data.serviceProvider;
        const profileAny = profile as any;
        
        // Extract device information from API response
        // Check for deviceInfo object first, then individual fields, then 'device' object
        const deviceInfoFromResponse = profileAny.deviceInfo || profileAny.device || null;
        const hasDeviceInfo = deviceInfoFromResponse && (
          deviceInfoFromResponse.fcmToken || 
          deviceInfoFromResponse.deviceType || 
          deviceInfoFromResponse.deviceId
        );
        const hasLegacyDeviceFields = profileAny.fcmToken || profileAny.deviceType || profileAny.deviceId;
        
        const serviceProvider: ServiceProvider = {
          id: profile.id,
          fullName: profile.fullName,
          contactNumber: profile.contactNumber,
          address: profile.businessAddress || '',
          businessAddress: profile.businessAddress,
          registeredOn: profile.registeredOn,
          servicesOffered: profile.servicesOffered !== undefined && profile.servicesOffered !== null
            ? profile.servicesOffered
            : (profile.categories && Array.isArray(profile.categories) ? profile.categories.length : 0),
          status: profileAny.status || (profile.verificationStatus === 'verified' ? 1 : 0), // Use status field from API, fallback to verificationStatus
          serviceProviderType: profile.serviceProviderType,
          categories: profile.categories,
          email: profile.email,
          tradeName: profile.tradeName,
          specialization: profile.specialization,
          profileStatus: profile.profileStatus,
          paymentStatus: profile.paymentStatus,
          verificationStatus: profile.verificationStatus,
          documents: profile.documents,
          firstServiceCompleted: profileAny.firstServiceCompleted,
          firstServiceCompletedAt: profileAny.firstServiceCompletedAt,
          firstServiceCompletedOn: profileAny.firstServiceCompletedOn,
          serviceType: profileAny.serviceType || profile.serviceProviderType?.name,
          // Plan details from profile
          planId: profileAny.planId,
          plan: profileAny.plan,
          planValidityUntil: profileAny.planValidityUntil,
          isPlanExpired: profileAny.isPlanExpired,
          paymentDetails: profileAny.paymentDetails,
          // Map device-related fields from profile API response
          deviceInfo: hasDeviceInfo ? {
            fcmToken: deviceInfoFromResponse.fcmToken || profileAny.fcmToken,
            deviceType: deviceInfoFromResponse.deviceType || profileAny.deviceType,
            deviceId: deviceInfoFromResponse.deviceId || profileAny.deviceId,
            deviceModel: deviceInfoFromResponse.deviceModel || profileAny.deviceModel,
            osVersion: deviceInfoFromResponse.osVersion || profileAny.osVersion,
          } : (hasLegacyDeviceFields ? {
            fcmToken: profileAny.fcmToken,
            deviceType: profileAny.deviceType,
            deviceId: profileAny.deviceId,
            deviceModel: profileAny.deviceModel,
            osVersion: profileAny.osVersion,
          } : undefined),
          // Legacy device fields for backward compatibility
          fcmToken: deviceInfoFromResponse?.fcmToken || profileAny.fcmToken,
          deviceType: deviceInfoFromResponse?.deviceType || profileAny.deviceType,
          deviceId: deviceInfoFromResponse?.deviceId || profileAny.deviceId,
          deviceModel: deviceInfoFromResponse?.deviceModel || profileAny.deviceModel,
          osVersion: deviceInfoFromResponse?.osVersion || profileAny.osVersion,
        };
        // Pass the full profile data to the detail view
        onViewMechanic(serviceProvider);
      } else {
        // Fallback to using the basic mechanic data if profile fetch fails
        console.warn('⚠️ Could not fetch full profile, using basic data');
        onViewMechanic(mechanic);
      }
    } catch (error) {
      console.error('❌ Error fetching service provider profile:', error);
      // Fallback to using the basic mechanic data if profile fetch fails
      onViewMechanic(mechanic);
    }
  };

  const handleStatusUpdate = (mechanic: Mechanic) => {
    setSelectedMechanic(mechanic);
    setStatusModalOpen(true);
    setSuccessMessage(null);
  };


  const handleStatusUpdateSubmit = async (mobileNumber: string, status: 'enabled' | 'disabled' | 'blocked' | 'unblocked', reason?: string) => {
    try {
      const response = await updateServiceProviderStatus(mobileNumber, status, reason);
      setSuccessMessage(response.message);
      setStatusModalOpen(false);
      setSelectedMechanic(null);
      
      // Clear success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
    } catch (error) {
      // Error is handled by the modal component
      throw error;
    }
  };

  const handleCloseStatusModal = () => {
    setStatusModalOpen(false);
    setSelectedMechanic(null);
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-slate-950 px-5 py-6 shadow-lg shadow-slate-950/10 sm:px-6">
        <div className="pointer-events-none absolute -right-20 -top-24 h-64 w-64 rounded-full bg-blue-500/20 blur-3xl" aria-hidden="true" />
        <div className="pointer-events-none absolute -bottom-24 left-1/3 h-48 w-48 rounded-full bg-cyan-400/10 blur-3xl" aria-hidden="true" />
        <div className="relative flex flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-blue-300/20 bg-blue-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-blue-200">
              <span className="h-1.5 w-1.5 rounded-full bg-cyan-300" aria-hidden="true" />
              Provider network
            </div>
            <h1 className="text-2xl font-semibold tracking-tight text-white sm:text-3xl">Registered Service Providers</h1>
            <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-300">Monitor provider growth, account status, and service coverage from one workspace.</p>
          </div>
          <button
            type="button"
            onClick={refreshData}
            disabled={loading}
            className="inline-flex h-10 items-center justify-center rounded-xl border border-white/15 bg-white/10 px-4 text-sm font-semibold text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-white/15 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-300 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} aria-hidden="true" />
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>
      </section>

      {/* Stats Cards */}
      <section className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4" aria-label="Service provider summary">
        <MetricCard label="All providers" value={loading ? '…' : (stats?.total || mechanics.length).toLocaleString()} helper="Registered accounts" icon={UsersRound} iconClassName="text-blue-700" iconBackgroundClassName="bg-blue-50" />
        <MetricCard label="Active" value={loading ? '…' : (stats?.enabled || mechanics.filter(m => m.status === 1 || m.status === 'enabled').length).toLocaleString()} helper="Enabled providers" icon={CheckCircle2} iconClassName="text-emerald-700" iconBackgroundClassName="bg-emerald-50" />
        <MetricCard label="Month to date" value={loading ? '…' : (stats?.monthToDate || 0).toLocaleString()} helper="New this month" icon={CalendarDays} iconClassName="text-violet-700" iconBackgroundClassName="bg-violet-50" />
        <MetricCard label="Year to date" value={loading ? '…' : (stats?.yearToDate || 0).toLocaleString()} helper="New this year" icon={Clock3} iconClassName="text-amber-700" iconBackgroundClassName="bg-amber-50" />
      </section>

      {/* Success Message */}
      {successMessage && (
        <div className="flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-800 shadow-sm" role="status">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <p className="text-sm font-medium">{successMessage}</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="flex items-start justify-between gap-4 rounded-xl border border-red-200 bg-red-50 p-4 shadow-sm" role="alert">
          <div className="flex items-start gap-3">
            <CircleAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
            <div><p className="text-sm font-semibold text-red-900">Unable to load service providers</p><p className="mt-1 text-sm text-red-700">{error}</p></div>
          </div>
          <button type="button" onClick={refreshData} className="shrink-0 rounded-lg border border-red-200 bg-white px-3 py-2 text-sm font-semibold text-red-700 transition-colors hover:bg-red-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2">Try again</button>
        </div>
      )}

      {/* Mechanics Table */}
      <div className="admin-table-shell">
        <div className="flex flex-col gap-4 border-b border-slate-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-5">
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><UsersRound className="h-[18px] w-[18px]" aria-hidden="true" /></span>
            <div><h2 className="text-base font-semibold text-slate-950">Provider directory</h2><p className="mt-0.5 text-xs text-slate-500" aria-live="polite">Showing {totalItems === 0 ? 0 : startIndex + 1}–{Math.min(endIndex, totalItems)} of {totalItems} providers</p></div>
          </div>
          <button type="button" onClick={() => setAreFiltersOpen(current => !current)} aria-expanded={areFiltersOpen} aria-controls="provider-filters" className={`inline-flex h-10 items-center justify-center rounded-xl border px-4 text-sm font-semibold shadow-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${areFiltersOpen || activeFilterCount > 0 ? 'border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50'}`}>
            <SlidersHorizontal className="mr-2 h-4 w-4" aria-hidden="true" />Filters
            {activeFilterCount > 0 && <span className="ml-2 inline-flex min-w-5 items-center justify-center rounded-full bg-blue-700 px-1.5 py-0.5 text-[11px] font-bold leading-none text-white">{activeFilterCount}</span>}
            <ChevronDown className={`ml-2 h-4 w-4 transition-transform ${areFiltersOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
          </button>
        </div>
        <div id="provider-filters" hidden={!areFiltersOpen} className="border-b border-slate-200 bg-slate-50/80 px-4 py-5 sm:px-5">
          <div className="flex flex-col gap-4 sm:flex-row">
            <div className="flex-1"><label htmlFor="provider-search" className="mb-1.5 block text-xs font-semibold text-slate-600">Search providers</label><div className="relative"><Search className="pointer-events-none absolute left-3.5 top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-slate-400" aria-hidden="true" /><input id="provider-search" type="search" placeholder="Name, ID, contact number, or address" className={`${filterControlClassName} pl-10`} value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)} /></div></div>
            <div className="sm:w-52"><label htmlFor="provider-status" className="mb-1.5 block text-xs font-semibold text-slate-600">Status</label><select id="provider-status" className={filterControlClassName} value={statusFilter} onChange={(event) => setStatusFilter(event.target.value as typeof statusFilter)}><option value="all">All statuses</option><option value="enabled">Enabled</option><option value="disabled">Disabled</option><option value="blocked">Blocked</option></select></div>
            <div className="sm:w-36"><label htmlFor="provider-items-per-page" className="mb-1.5 block text-xs font-semibold text-slate-600">Rows</label><select id="provider-items-per-page" className={filterControlClassName} value={itemsPerPage} onChange={(event) => { setItemsPerPage(Number(event.target.value)); setCurrentPage(1); }}><option value={5}>5 per page</option><option value={10}>10 per page</option><option value={25}>25 per page</option><option value={50}>50 per page</option></select></div>
          </div>
          {activeFilterCount > 0 && <div className="mt-4 flex justify-end"><button type="button" onClick={() => { setSearchTerm(''); setStatusFilter('all'); setCurrentPage(1); }} className="inline-flex h-10 items-center justify-center rounded-xl border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2"><X className="mr-2 h-4 w-4" aria-hidden="true" />Clear filters</button></div>}
        </div>
        {loading && (
          <div className="flex flex-col items-center justify-center py-14">
            <LoaderCircle className="mb-3 h-8 w-8 animate-spin text-blue-600" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-slate-900">Loading service providers…</h3>
            <p className="mt-1 text-sm text-slate-500">Fetching the latest provider records.</p>
          </div>
        )}
        
        {!loading && (
          <div className="admin-table-scroll" role="region" aria-label="Registered service providers data table" tabIndex={0}>
            <table className="admin-data-table min-w-max">
            <thead>
              <tr>
                <th 
                  onClick={() => handleSort('status')}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('status'); }}
                  tabIndex={0}
                >
                  <div className="flex items-center space-x-1">
                    <span>Status</span>
                    {getSortIcon('status')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('fullName')}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('fullName'); }}
                  tabIndex={0}
                >
                  <div className="flex items-center space-x-1">
                    <span>Full Name</span>
                    {getSortIcon('fullName')}
                  </div>
                </th>
                <th>
                  Contact Number
                </th>
                <th 
                  onClick={() => handleSort('registeredOn')}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('registeredOn'); }}
                  tabIndex={0}
                >
                  <div className="flex items-center space-x-1">
                    <span>Registered On</span>
                    {getSortIcon('registeredOn')}
                  </div>
                </th>
                <th 
                  onClick={() => handleSort('servicesOffered')}
                  onKeyDown={(event) => { if (event.key === 'Enter' || event.key === ' ') handleSort('servicesOffered'); }}
                  tabIndex={0}
                >
                  <div className="flex items-center space-x-1">
                    <span>Services Offered</span>
                    {getSortIcon('servicesOffered')}
                  </div>
                </th>
                <th>
                  Service Type
                </th>
                <th>
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {paginatedMechanics.map((mechanic, index) => (
                  <tr key={mechanic.id || `mechanic-${index}`}>
                    <td className="whitespace-nowrap">
                      <span className={`inline-flex rounded-full border px-2.5 py-1 text-xs font-semibold ${getStatusBadgeClass(mechanic.status)}`}>
                        {getStatusText(mechanic.status)}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">
                      <div className="font-medium text-slate-900">{mechanic.fullName}</div>
                    </td>
                    <td className="whitespace-nowrap">
                      <div className="text-slate-700">{mechanic.contactNumber}</div>
                    </td>
                    <td className="whitespace-nowrap">
                      <div className="text-slate-700">{formatDateTimeString(mechanic.registeredOn)}</div>
                    </td>
                    <td className="whitespace-nowrap">
                      <span className="inline-flex h-8 min-w-8 items-center justify-center rounded-full bg-blue-50 px-2 text-sm font-semibold text-blue-700">
                        {mechanic.servicesOffered.toString().padStart(2, '0')}
                      </span>
                    </td>
                    <td className="whitespace-nowrap">
                      <div className="text-slate-700">
                        {mechanic.serviceProviderType?.name || 'Not specified'}
                      </div>
                    </td>
                    <td className="whitespace-nowrap">
                      <div className="flex gap-2">
                        <button
                          type="button"
                          onClick={() => handleViewMechanic(mechanic)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 shadow-sm transition-colors hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
                          aria-label={`View ${mechanic.fullName}`}
                          title={`View ${mechanic.fullName}`}
                        >
                          <Eye className="h-4 w-4" aria-hidden="true" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleStatusUpdate(mechanic)}
                          disabled={isUpdatingStatus}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 shadow-sm transition-colors hover:bg-blue-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                          aria-label={`Update status for ${mechanic.fullName}`}
                          title={`Update status for ${mechanic.fullName}`}
                        >
                          <Settings2 className="h-4 w-4" aria-hidden="true" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
        )}

        {/* Empty State */}
        {!loading && paginatedMechanics.length === 0 && (
          <div className="flex flex-col items-center py-14 text-center">
            <span className="mb-3 flex h-11 w-11 items-center justify-center rounded-xl bg-slate-100 text-slate-500"><UsersRound className="h-5 w-5" aria-hidden="true" /></span>
            <h3 className="text-sm font-semibold text-slate-900">No service providers found</h3>
            <p className="mt-1 text-sm text-slate-500">Try adjusting your search or status filter.</p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="admin-table-pagination">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="inline-flex h-10 items-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Previous
            </button>
            <button
              type="button"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
              className="inline-flex h-10 items-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-slate-600">
                Showing <span className="font-medium">{startIndex + 1}</span> to{' '}
                <span className="font-medium">{Math.min(endIndex, totalItems)}</span> of{' '}
                <span className="font-medium">{totalItems}</span> results
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                {/* Previous Button */}
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="inline-flex items-center justify-center bg-white text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <span className="sr-only">Previous page</span>
                  <ChevronLeft className="h-4 w-4" aria-hidden="true" />
                </button>

                {/* Page Numbers */}
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
                  
                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  // First page
                  if (startPage > 1) {
                    pages.push(
                      <button
                        type="button"
                        key={1}
                        onClick={() => setCurrentPage(1)}
                        className="bg-white font-medium text-slate-700 hover:bg-slate-50"
                      >
                        1
                      </button>
                    );
                    if (startPage > 2) {
                      pages.push(
                        <span key="ellipsis1" className="inline-flex items-center justify-center bg-white text-sm text-slate-400">
                          ...
                        </span>
                      );
                    }
                  }

                  // Visible pages
                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        type="button"
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        aria-current={i === currentPage ? 'page' : undefined}
                        className={`text-sm ${
                          i === currentPage
                            ? 'bg-slate-900 font-semibold text-white'
                            : 'bg-white font-medium text-slate-700 hover:bg-slate-50'
                        }`}
                      >
                        {i}
                      </button>
                    );
                  }

                  // Last page
                  if (endPage < totalPages) {
                    if (endPage < totalPages - 1) {
                      pages.push(
                        <span key="ellipsis2" className="inline-flex items-center justify-center bg-white text-sm text-slate-400">
                          ...
                        </span>
                      );
                    }
                    pages.push(
                      <button
                        type="button"
                        key={totalPages}
                        onClick={() => setCurrentPage(totalPages)}
                        className="bg-white font-medium text-slate-700 hover:bg-slate-50"
                      >
                        {totalPages}
                      </button>
                    );
                  }

                  return pages;
                })()}

                {/* Next Button */}
                <button
                  type="button"
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  className="inline-flex items-center justify-center bg-white text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <span className="sr-only">Next page</span>
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      <StatusUpdateModal
        isOpen={statusModalOpen}
        onClose={handleCloseStatusModal}
        serviceProvider={selectedMechanic}
        onStatusUpdate={handleStatusUpdateSubmit}
        isUpdating={isUpdatingStatus}
      />
    </div>
  );
};
